import React from 'react';
import {
  TextField,
  InputAdornment,
  Box,
  IconButton,
} from '@mui/material';
import type { Color as Interface } from '../../design/components';
import { FontIcon } from './FontIcon';

export const Color: React.FC<Interface> = ({
  name,
  description,
  state,
  placeholder = 'Select a color',
  hidden,
  inline,
  readonly,
  disabled,
  required,
  error,
}) => {
  const [value, setValue] = state || ['', () => {}];

  // Inline mode: render just the color swatch
  if (inline) {
    return value ? (
      <Box
        sx={{
          display: hidden ? 'none' : 'inline-block',
          width: 20,
          height: 20,
          borderRadius: 1,
          bgcolor: value,
          border: 1,
          borderColor: 'divider',
        }}
      />
    ) : null;
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    setValue('');
  };

  return (
    <Box sx={{ display: hidden ? 'none' : undefined }}>
      <TextField
        id={name}
        name={name}
        label={name}
        value={value || ''}
        placeholder={placeholder}
        error={!!error}
        disabled={disabled}
        required={required}
        fullWidth
        helperText={error || description}
        slotProps={{
          input: {
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <Box
                  component="input"
                  type="color"
                  value={value || '#000000'}
                  onChange={handleChange}
                  disabled={disabled || readonly}
                  sx={{
                    width: 32,
                    height: 32,
                    p: 0,
                    border: 'none',
                    bgcolor: 'transparent',
                    cursor: readonly || disabled ? 'default' : 'pointer',
                    '&::-webkit-color-swatch-wrapper': { p: 0 },
                    '&::-webkit-color-swatch': { border: 1, borderColor: 'divider', borderRadius: 1 },
                  }}
                />
              </InputAdornment>
            ),
            endAdornment: value && !readonly && !disabled ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClear} aria-label="Clear color">
                  <FontIcon name="clear" size="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          },
        }}
        sx={{
          cursor: readonly || disabled ? 'default' : 'pointer',
          '& .MuiInputBase-input': {
            cursor: readonly || disabled ? 'default' : 'pointer',
          },
        }}
      />
    </Box>
  );
};
